import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Users, Loader2, RefreshCw, GitMerge } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLLECTION_LABELS = {
  applications: '申込',
  certificate_requests: '修了証',
  invoices: '請求書',
  practical_reservations: '実技予約',
  quiz_attempts: 'クイズ回答',
  user_progress: '進捗',
  video_progress: '動画進捗',
  monitoring_logs: '監視ログ',
};

export const DuplicateUsersDialog = ({ open, onOpenChange, authHeaders, onChanged }) => {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [primaryByGroup, setPrimaryByGroup] = useState({});
  const [counts, setCounts] = useState({});
  const [merging, setMerging] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/duplicate-users`, { headers: authHeaders() });
      const data = res.data || [];
      setGroups(data);
      const defaults = {};
      data.forEach((g, idx) => { defaults[idx] = g.users[0]?.id; });
      setPrimaryByGroup(defaults);
      // 各ユーザーの関連データ件数を取得（プレビュー用）
      const allIds = [...new Set(data.flatMap(g => g.users.map(u => u.id)))];
      const results = await Promise.all(allIds.map(id =>
        axios.get(`${API}/admin/users/${id}/related-counts`, { headers: authHeaders() })
          .then(r => [id, r.data.counts]).catch(() => [id, null])
      ));
      setCounts(Object.fromEntries(results));
    } catch {
      toast.error('重複アカウントの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (open) fetchData();
  }, [open, fetchData]);

  const totalRecords = (id) => {
    const c = counts[id];
    if (!c) return 0;
    return Object.values(c).reduce((a, b) => a + b, 0);
  };

  const mergeGroup = async (group, groupIdx) => {
    const primaryId = primaryByGroup[groupIdx];
    if (!primaryId) return;
    const secondaries = group.users.filter(u => u.id !== primaryId);
    const primary = group.users.find(u => u.id === primaryId);
    const names = secondaries.map(u => `${u.name}（${u.email}）`).join('\n');
    if (!window.confirm(
      `以下のアカウントを「${primary.name}（${primary.email}）」に統合します。\n\n${names}\n\n` +
      `統合すると、上記アカウントの全データ（申込・修了証・請求書など）が主アカウントへ移り、上記アカウントは削除されます。\nこの操作は元に戻せません。実行しますか？`
    )) return;
    setMerging(true);
    try {
      for (const s of secondaries) {
        await axios.post(`${API}/admin/users/merge`, { primaryId, secondaryId: s.id }, { headers: authHeaders() });
      }
      toast.success(`${secondaries.length}件のアカウントを統合しました`);
      onChanged && onChanged();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || '統合に失敗しました');
    } finally {
      setMerging(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="duplicate-users-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-[#00A651]" />重複アカウントの整理
          </DialogTitle>
          <DialogDescription>
            同じメール・氏名で重複登録されたアカウントを検出します。主アカウントを選んで統合してください。
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-600" data-testid="dup-group-count">重複グループ: {groups.length}件</span>
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="dup-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1" />更新
          </Button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-gray-400">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />読み込み中...
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm" data-testid="dup-empty">
              重複アカウントは見つかりませんでした 🎉
            </div>
          ) : groups.map((g, idx) => (
            <div key={idx} className="border rounded-lg p-3 bg-white" data-testid={`dup-group-${idx}`}>
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-blue-100 text-blue-700 text-xs">
                  {g.matchType === 'email' ? 'メール一致' : '氏名一致'}
                </Badge>
                <span className="text-xs text-gray-500 truncate">{g.matchValue}</span>
              </div>
              <div className="space-y-1.5">
                {g.users.map(u => (
                  <label key={u.id} className={`flex items-center justify-between gap-2 rounded px-2 py-2 cursor-pointer border ${primaryByGroup[idx] === u.id ? 'border-[#00A651] bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <input type="radio" name={`primary-${idx}`} checked={primaryByGroup[idx] === u.id}
                        onChange={() => setPrimaryByGroup(p => ({ ...p, [idx]: u.id }))}
                        data-testid={`dup-primary-${idx}-${u.id}`} className="accent-[#00A651]" />
                      <div className="min-w-0">
                        <p className="text-xs font-medium truncate">{u.name}
                          {primaryByGroup[idx] === u.id && <span className="ml-1 text-[10px] text-[#00A651]">（主）</span>}
                        </p>
                        <p className="text-[11px] text-gray-500 truncate">{u.email}{u.phone ? `・${u.phone}` : ''}</p>
                        <p className="text-[10px] text-gray-400 truncate">
                          登録: {(u.createdAt || '').substring(0, 10)}・関連データ {totalRecords(u.id)}件
                        </p>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              <div className="flex justify-end mt-2">
                <Button size="sm" className="bg-[#00A651] hover:bg-[#008a43] h-8 text-xs"
                  disabled={merging}
                  data-testid={`dup-merge-${idx}`}
                  onClick={() => mergeGroup(g, idx)}>
                  {merging ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><GitMerge className="h-3.5 w-3.5 mr-1" />主アカウントに統合</>}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateUsersDialog;
